import React, { useState, useRef, useEffect } from 'react';
import { X, UploadSimple, FileCsv, ArrowLeft, Database, UserCircle, Check, CaretDown } from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import { useNotifications } from '../../../contexts/NotificationContext';
import { getApiBaseUrl } from '../../../core/api/apiClient';
import { toast } from 'sonner';
import posthog from '../../../posthog';

interface ImportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

export const PROFILE_TARGET_FIELDS = [
  // ── Required / Recommended ──
  { key: 'donor_id', label: 'Alumni/CSR Id', required: true, group: 'Required' },
  { key: 'current_name', label: 'Current Name', required: true, group: 'Required' },
  { key: 'linked_in', label: 'LinkedIn URL', required: false, group: 'Required' },
  { key: 'designation', label: 'Designation', required: false, group: 'Required' },
  { key: 'company', label: 'Company', required: false, group: 'Required' },
  { key: 'work_country', label: 'Work Country', required: false, group: 'Required' },
  // ── Identity ──
  { key: 'is_alumni', label: 'Is Alumni?', required: false, group: 'Identity' },
  { key: 'gender', label: 'Person Account: Gender', required: false, group: 'Identity' },
  { key: 'degree', label: '*Degree', required: false, group: 'Identity' },
  { key: 'additional_iit_degree', label: 'Additional IIT Degree', required: false, group: 'Identity' },
  { key: 'department', label: '*Department', required: false, group: 'Identity' },
  { key: 'batch', label: '*Batch', required: false, group: 'Identity' },
  { key: 'hostel1', label: 'Hostel 1', required: false, group: 'Identity' },
  { key: 'hostel2', label: 'Hostel 2', required: false, group: 'Identity' },
  { key: 'hostel3', label: 'Hostel 3', required: false, group: 'Identity' },
  { key: 'is_a_faculty', label: 'Is a Faculty?', required: false, group: 'Identity' },
  { key: 'retired_faculty_iitm', label: 'Retired Faculty(IITM)', required: false, group: 'Identity' },
  // ── Professional ──
  { key: 'work_address_state_province', label: 'Person Account: Work Address (State/Province)', required: false, group: 'Professional' },
  { key: 'work_address_city', label: 'Person Account: Work Address (City)', required: false, group: 'Professional' },
  { key: 'work_address_country_territory', label: 'Person Account: Work Address (Country/Territory)', required: false, group: 'Professional' },
  { key: 'account_record_type', label: 'Account Record Type', required: false, group: 'Professional' },
  { key: 'corporate_contact_category', label: 'Person Account: Corporate Contact Category', required: false, group: 'Professional' },
  // ── Giving History ──
  { key: 'cumulative_giving_to_iitm', label: 'Cumulative Giving to IITM', required: false, group: 'Giving History' },
  { key: 'largest_gift_to_iitm', label: 'Largest Gift to IITM', required: false, group: 'Giving History' },
  { key: 'smallest_gift', label: 'Smallest Gift', required: false, group: 'Giving History' },
  { key: 'date_of_first_gift_to_iitm', label: 'Date of First Gift to IITM', required: false, group: 'Giving History' },
  { key: 'date_of_last_gift_to_iitm', label: 'Date of Last Gift to IITM', required: false, group: 'Giving History' },
  { key: 'total_number_of_payments', label: 'Total Number of Payments', required: false, group: 'Giving History' },
  { key: 'last_cause_supported', label: 'Last Cause Supported', required: false, group: 'Giving History' },
  { key: 'last_touch', label: 'Last Touch', required: false, group: 'Giving History' },
  { key: 'total_donation_pledged', label: 'Total Donation Pledged', required: false, group: 'Giving History' },
  { key: 'amount_received_till_date', label: 'Amount Received Till Date', required: false, group: 'Giving History' },
  { key: 'company_contribution', label: 'Company Contribution', required: false, group: 'Giving History' },
  { key: 'net_contribution', label: 'Net Contribution', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy21', label: 'Amount Donated in the FY21', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy22', label: 'Amount Donated in the FY22', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy23', label: 'Amount Donated in the FY23', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy24', label: 'Amount Donated in the FY24', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy25', label: 'Amount Donated in the FY25', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy26', label: 'Amount Donated in the FY26', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy27', label: 'Amount Donated in the FY27', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy28', label: 'Amount Donated in the FY28', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy29', label: 'Amount Donated in the FY29', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy30', label: 'Amount Donated in the FY30', required: false, group: 'Giving History' },
  { key: 'amount_donated_in_the_fy31', label: 'Amount Donated in the FY31', required: false, group: 'Giving History' },
  // ── Prospect / Stewardship ──
  { key: 'salesforce_object', label: 'Salesforce Object', required: false, group: 'Prospect' },
  { key: 'key_account_holder_donor_stewardship', label: 'Key Account Holder Donor Stewardship', required: false, group: 'Prospect' },
  { key: 'sales_person', label: 'Sales Person', required: false, group: 'Prospect' },
  { key: 'account_leader', label: 'Account Leader', required: false, group: 'Prospect' },
  { key: 'gift_opportunity', label: 'Gift Opportunity', required: false, group: 'Prospect' },
  { key: 'current_gift_target', label: 'Current Gift Target', required: false, group: 'Prospect' },
  { key: 'readiness_to_give', label: 'Readiness to Give', required: false, group: 'Prospect' },
  { key: 'probability', label: 'Probability', required: false, group: 'Prospect' },
  { key: 'expected_gift_amount', label: 'Expected Gift Amount', required: false, group: 'Prospect' },
  { key: 'ask_date', label: 'Ask Date', required: false, group: 'Prospect' },
  { key: 'expected_date', label: 'Expected Date', required: false, group: 'Prospect' },
  { key: 'giving_potential_over_5_years', label: 'Giving Potential Over 5 Years', required: false, group: 'Prospect' },
  { key: 'hni', label: 'HNI', required: false, group: 'Prospect' },
  { key: 'potential_hni', label: 'Potential HNI', required: false, group: 'Prospect' },
  { key: 'hni_type', label: 'HNI-Type', required: false, group: 'Prospect' },
  { key: 'hni_category', label: 'HNI Category', required: false, group: 'Prospect' },
  { key: 'hni_stage', label: 'HNI Stage', required: false, group: 'Prospect' },
  { key: 'daa', label: 'DAA', required: false, group: 'Prospect' },
  { key: 'do_not_solicit', label: 'Do Not Solicit', required: false, group: 'Prospect' },
  { key: 'status', label: 'Status', required: false, group: 'Prospect' },
  { key: 'account_status', label: 'Account Status', required: false, group: 'Prospect' },
  // ── Interests & Engagement ──
  { key: 'philanthropic_interests', label: 'Philanthropic Interests', required: false, group: 'Engagement' },
  { key: 'interest', label: 'Interest', required: false, group: 'Engagement' },
  { key: 'alumni_interest_at_iit', label: 'Alumni Interest at IIT', required: false, group: 'Engagement' },
  { key: 'position_of_responsibilities_iitm', label: 'Position of Responsibilities @ IITM', required: false, group: 'Engagement' },
  { key: 'sports_category', label: 'Sports Category', required: false, group: 'Engagement' },
  { key: 'roles_iitm', label: 'Roles @ IITM', required: false, group: 'Engagement' },
  { key: 'is_a_batch_co_odinator', label: 'Is a Batch Co-odinator', required: false, group: 'Engagement' },
  { key: 'volunteer_service', label: 'Volunteer Service', required: false, group: 'Engagement' },
  { key: 'lls_details', label: 'LLS Details', required: false, group: 'Engagement' },
  { key: 'travel_grant_beneficiary', label: 'Travel Grant Beneficiary', required: false, group: 'Engagement' },
  { key: 'donor_wall_of_frame', label: 'Donor Wall Of Frame', required: false, group: 'Engagement' },
  // ── Social & Contact ──
  { key: 'linkedin_name', label: 'LinkedIn Name', required: false, group: 'Social' },
  { key: 'mailing_city', label: 'Person Account: Mailing City', required: false, group: 'Social' },
  { key: 'mailing_state_province', label: 'Person Account: Mailing State/Province', required: false, group: 'Social' },
  { key: 'mailing_country', label: 'Person Account: Mailing Country', required: false, group: 'Social' },
  { key: 'msn', label: 'MSN', required: false, group: 'Social' },
  { key: 'yahoo', label: 'Yahoo', required: false, group: 'Social' },
  { key: 'facebook', label: 'Facebook', required: false, group: 'Social' },
  { key: 'twitter', label: 'Twitter', required: false, group: 'Social' },
  { key: 'website', label: 'Website', required: false, group: 'Social' },
  { key: 'others', label: 'Others', required: false, group: 'Social' },
  { key: 'liked_posts', label: 'Liked Posts', required: false, group: 'Social' },
  { key: 'recent_in_news', label: 'Recent in NEWS', required: false, group: 'Social' },
  // ── Misc ──
  { key: 'last_modified_by', label: 'Last Modified By', required: false, group: 'Misc' },
  { key: 'account_18_digit_id', label: 'Account 18 Digit Id', required: false, group: 'Misc' },
];

const PAYMENTS_TARGET_FIELDS = [
  { key: 'date', label: 'Payment Date', required: false, group: 'Payment Details' },
  { key: 'closure_date', label: 'Closure Date', required: false, group: 'Payment Details' },
  { key: 'donor_name', label: 'Donated by: Current Name', required: false, group: 'Payment Details' },
  { key: 'donor_category', label: 'Donor Category', required: false, group: 'Payment Details' },
  { key: 'receipt_category', label: 'Receipt Category', required: false, group: 'Payment Details' },
  { key: 'project', label: 'Donation For', required: false, group: 'Payment Details' },
  { key: 'amount', label: 'Payment Amount', required: false, group: 'Payment Details' },
  { key: 'currency', label: 'Currency', required: false, group: 'Payment Details' },
  { key: 'stage', label: 'Stage', required: false, group: 'Payment Details' },
  { key: 'probability', label: 'Probability (%)', required: false, group: 'Payment Details' },
  { key: 'transfer_type', label: 'Transfer Type', required: false, group: 'Payment Details' },
  { key: 'donor_id', label: 'Donor Id', required: true, group: 'Required' },
  { key: 'payment_id', label: 'Payment Number', required: true, group: 'Required' },
  { key: 'paid', label: 'Paid', required: false, group: 'Payment Details' },
  { key: 'donation_through', label: 'Donation Through', required: false, group: 'Payment Details' },
  { key: 'type', label: 'Type(Project/Endowment)', required: false, group: 'Payment Details' },
  { key: 'salesforce_object', label: 'Payment ID', required: false, group: 'Payment Details' },
  { key: 'cause_id', label: 'Cause Number', required: false, group: 'Payment Details' },
];

const STEPS = ['Select Type', 'Upload File', 'Map Fields', 'Analyze & Import'];

export function ImportDataModal({ isOpen, onClose, onImportSuccess }: ImportDataModalProps) {
  const [step, setStep] = useState<'select' | 'upload' | 'mapping' | 'analysis'>('select');
  const [importType, setImportType] = useState<'payments' | 'profile' | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<{ new: number; existing: number; missing_donor: number; total: number } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);

  const { addUploadTask, tasks } = useNotifications();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const API_BASE_URL = getApiBaseUrl();

  const activeTask = activeTaskId ? tasks.find((t) => t.id === activeTaskId) : null;

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setImportType(null);
      setActiveTaskId(null);
      setFile(null);
      setCsvColumns([]);
      setMappings({});
      setUpdateExisting(false);
      setAnalysisResult(null);
      setShowAllFields(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (activeTask && activeTask.status === 'processing') {
      toast.info(`${file?.name || 'File'} is continuing to upload in the background.`);
    }
    if (activeTask && activeTask.status === 'completed') {
      onImportSuccess?.();
    }
    onClose();
  };

  const validateAndSetFile = (selectedFile: File | null) => {
    if (!selectedFile) return;
    const validExts = ['.csv', '.xlsx', '.xls'];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (validExts.includes(ext)) {
      setFile(selectedFile);
    } else {
      toast.error('Please select a valid CSV or Excel file (.csv, .xlsx, .xls)');
    }
  };

  const fetchColumns = async () => {
    if (!file) return;
    setIsLoadingColumns(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/import/column_mappings`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to fetch columns');
      const data = await response.json();

      if (data.status === 'success') {
        const columns = data.data as string[];
        setCsvColumns(columns);
        const columnsLower = columns.map((c) => c.toLowerCase().trim());
        const targetFields = importType === 'payments' ? PAYMENTS_TARGET_FIELDS : PROFILE_TARGET_FIELDS;
        const newMappings: Record<string, string> = {};
        targetFields.forEach((field) => {
          const fieldLower = field.label.toLowerCase().trim();
          const matchIndex = columnsLower.findIndex((c) => c === fieldLower || c === field.key.toLowerCase());
          if (matchIndex !== -1) newMappings[field.key] = columns[matchIndex];
        });
        setMappings(newMappings);
        setStep('mapping');
      } else {
        throw new Error(data.message || 'Error parsing columns');
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not parse columns from file');
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    const targetFields = importType === 'payments' ? PAYMENTS_TARGET_FIELDS : PROFILE_TARGET_FIELDS;
    const missingRequired = targetFields.filter((f) => f.required && !mappings[f.key]);
    if (missingRequired.length > 0) {
      toast.error(`Please map required fields: ${missingRequired.map((f) => f.label).join(', ')}`);
      return;
    }
    setIsAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    formData.append('import_type', importType || 'profile');

    try {
      const response = await fetch(`${API_BASE_URL}/import/analyze`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Analysis failed');
      }
      const data = await response.json();
      setAnalysisResult({ new: data.new, existing: data.existing, missing_donor: data.missing_donor ?? 0, total: data.total_valid });
      setStep('analysis');
    } catch (err: any) {
      toast.error(err.message || 'Could not analyze data');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setActiveTaskId('starting');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    formData.append('update_existing', updateExisting.toString());

    try {
      const endpoint = importType === 'profile' ? '/import/profile_data' : '/import/monday_data';
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }
      const data = await response.json();
      if (data.task_id) {
        posthog.capture('data_import_started', {
          import_type: importType || 'profile',
          update_existing: updateExisting,
        });
        addUploadTask(data.task_id, file.name);
        setActiveTaskId(data.task_id);
      } else {
        throw new Error('No task ID returned from server');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload file');
      setActiveTaskId(null);
    }
  };

  const stepIndex = { select: 0, upload: 1, mapping: 2, analysis: 3 }[step];
  const targetFields = importType === 'payments' ? PAYMENTS_TARGET_FIELDS : PROFILE_TARGET_FIELDS;

  // For profile mapping: split into auto-matched/required vs optional unmapped
  const requiredAndMatched = targetFields.filter((f) => f.required || mappings[f.key]);
  const optionalUnmapped = targetFields.filter((f) => !f.required && !mappings[f.key]);
  const visibleFields = (showAllFields || importType === 'payments') ? targetFields : requiredAndMatched;

  const MappingSelect = ({ field }: { field: (typeof targetFields)[0] }) => (
    <div className="flex items-center justify-between gap-3 py-2.5 px-3 border-b border-slate-100 last:border-b-0">
      <div className="min-w-0 flex-1">
        <span className="text-xs font-semibold text-slate-700 truncate block">
          {field.label}
          {field.required && <span className="text-rose-500 ml-0.5">*</span>}
        </span>
        {field.group && (
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{field.group}</span>
        )}
      </div>
      <select
        className="w-96 shrink-0 text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
        value={mappings[field.key] || ''}
        onChange={(e) => setMappings((prev) => ({ ...prev, [field.key]: e.target.value }))}
      >
        <option value="">— Not mapped —</option>
        {csvColumns.map((col) => (
          <option key={col} value={col}>{col}</option>
        ))}
      </select>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-xl w-full overflow-hidden flex flex-col font-sans animate-in zoom-in-95 duration-200 max-h-[90vh] transition-all ${
          step === 'mapping' ? 'max-w-4xl' : 'max-w-lg'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60 shrink-0">
          <div className="flex items-center gap-3">
            {step !== 'select' && !activeTaskId && (
              <button
                onClick={() => (step === 'mapping' ? setStep('upload') : step === 'analysis' ? setStep('mapping') : setStep('select'))}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft size={16} weight="bold" />
              </button>
            )}
            <div>
              <h2 className="text-sm font-outfit font-bold text-slate-800">
                {step === 'select'
                  ? 'Import Data'
                  : importType === 'payments'
                  ? 'Import Payments Data'
                  : 'Import Profile Data'}
              </h2>
              {!activeTaskId && (
                <p className="text-[10px] font-semibold text-slate-400 mt-0.5">
                  {STEPS[stepIndex]}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* ── Step Progress Bar ── */}
        {!activeTaskId && (
          <div className="px-6 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-1.5">
              {STEPS.map((label, idx) => (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black transition-all ${
                        idx < stepIndex
                          ? 'bg-accent text-white'
                          : idx === stepIndex
                          ? 'bg-accent text-white ring-4 ring-accent/15'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {idx < stepIndex ? <Check size={10} weight="bold" /> : idx + 1}
                    </div>
                    <span
                      className={`text-[10px] font-bold whitespace-nowrap hidden sm:block ${
                        idx === stepIndex ? 'text-slate-800' : 'text-slate-400'
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-px ${idx < stepIndex ? 'bg-accent' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* ── Body ── */}
        <div className="p-6 overflow-y-auto flex-1">

          {/* ── Processing State ── */}
          {activeTaskId ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
              {activeTask?.status === 'processing' ? (
                <>
                  <div className="w-14 h-14 rounded-full border-4 border-slate-100 border-t-accent animate-spin mb-2" />
                  <p className="text-sm font-bold text-slate-800">Processing Data...</p>
                  <p className="text-xs font-semibold text-slate-400">
                    Row {activeTask.progress.current} of {activeTask.progress.total}
                  </p>
                  <div className="w-full max-w-xs h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all duration-300 ease-out rounded-full"
                      style={{
                        width: activeTask.progress.total > 0
                          ? `${(activeTask.progress.current / activeTask.progress.total) * 100}%`
                          : '5%',
                      }}
                    />
                  </div>
                </>
              ) : activeTask?.status === 'completed' ? (
                <>
                  <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 mb-2">
                    <Check size={28} weight="bold" />
                  </div>
                  <p className="text-sm font-bold text-emerald-700">Upload Complete!</p>
                  <p className="text-xs font-semibold text-slate-400">
                    Inserted: {activeTask.summary?.inserted || 0} · Skipped: {activeTask.summary?.skipped || 0}
                  </p>
                  
                  {activeTask.summary?.skipped_log && activeTask.summary.skipped_log.length > 0 && (
                    <div className="mt-4 w-full text-left bg-slate-50 border border-slate-200 rounded-xl p-3 max-h-60 overflow-y-auto shadow-inner">
                      <p className="text-xs font-bold text-slate-700 mb-2">Skipped Items Summary:</p>
                      
                      {(() => {
                        const groupedLogs = activeTask.summary.skipped_log.reduce((acc: Record<string, any[]>, log: any) => {
                          const reason = log.reason || 'Unknown Reason';
                          if (!acc[reason]) acc[reason] = [];
                          acc[reason].push(log);
                          return acc;
                        }, {});

                        return (
                          <div className="space-y-2">
                            {Object.entries(groupedLogs).map(([reason, logs]: [string, any[]]) => (
                              <details key={reason} className="bg-white border border-slate-200 rounded-lg group shadow-sm [&_summary::-webkit-details-marker]:hidden">
                                <summary className="text-xs font-bold text-slate-700 p-2.5 cursor-pointer list-none flex justify-between items-center hover:bg-slate-50 transition-colors rounded-lg group-open:rounded-b-none group-open:border-b group-open:border-slate-100">
                                  <div className="flex items-center gap-2">
                                    <CaretDown size={14} weight="bold" className="text-slate-400 group-open:rotate-180 transition-transform duration-200" />
                                    <span>{reason}</span>
                                  </div>
                                  <span className="bg-rose-50 text-rose-600 border border-rose-100 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">{logs.length} item{logs.length !== 1 ? 's' : ''}</span>
                                </summary>
                                <div className="p-2 max-h-40 overflow-y-auto bg-slate-50/50 rounded-b-lg">
                                  <ul className="text-[11px] text-slate-600 space-y-1.5">
                                    {logs.map((log: any, i: number) => (
                                      <li key={i} className="flex gap-2 p-2 rounded bg-white border border-slate-100 shadow-sm items-center">
                                        <span className="font-semibold text-slate-700 shrink-0 bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">Row {log.row}</span>
                                        {log.donor_id && <span className="text-slate-500 font-medium truncate">Donor ID: <span className="font-mono text-slate-700">{log.donor_id}</span></span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </details>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <Button onClick={handleClose} className="mt-2 shadow-sm">Close</Button>
                </>
              ) : activeTask?.status === 'error' ? (
                <>
                  <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 mb-2">
                    <X size={28} weight="bold" />
                  </div>
                  <p className="text-sm font-bold text-rose-600">Upload Failed</p>
                  <p className="text-xs text-rose-500 font-semibold">{activeTask.errorMsg || 'An error occurred'}</p>
                  <Button onClick={() => setActiveTaskId(null)} variant="outline" className="mt-2">Try Again</Button>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full border-4 border-slate-100 border-t-accent animate-spin mb-2" />
                  <p className="text-sm font-bold text-slate-800">Starting Upload...</p>
                  <p className="text-xs text-slate-400 font-semibold">Sending file to the server...</p>
                </>
              )}
            </div>

          ) : step === 'select' ? (
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { setImportType('payments'); setStep('upload'); }}
                className="w-full flex items-center p-5 rounded-xl border border-slate-200 hover:border-accent hover:bg-accent/5 transition-smooth group"
              >
                <div className="w-11 h-11 rounded-xl bg-slate-100 group-hover:bg-accent/10 flex items-center justify-center text-slate-500 group-hover:text-accent mr-4 transition-smooth shrink-0">
                  <Database size={22} weight="duotone" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-800 group-hover:text-accent transition-colors text-sm">Payments Data</h3>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">Upload donor transaction history from Salesforce/Monday</p>
                </div>
              </button>

              <button
                onClick={() => { setImportType('profile'); setStep('upload'); }}
                className="w-full flex items-center p-5 rounded-xl border border-slate-200 hover:border-accent hover:bg-accent/5 transition-smooth group"
              >
                <div className="w-11 h-11 rounded-xl bg-slate-100 group-hover:bg-accent/10 flex items-center justify-center text-slate-500 group-hover:text-accent mr-4 transition-smooth shrink-0">
                  <UserCircle size={22} weight="duotone" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-800 group-hover:text-accent transition-colors text-sm">Profile Data</h3>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">Upload alumni profile attributes and CRM data</p>
                </div>
              </button>
            </div>

          ) : step === 'upload' ? (
            <div className="flex flex-col gap-5">
              <div
                className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-smooth cursor-pointer ${
                  isDragging
                    ? 'border-accent bg-accent/5'
                    : file
                    ? 'border-emerald-300 bg-emerald-50/50'
                    : 'border-slate-200 hover:border-accent hover:bg-slate-50/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) validateAndSetFile(e.dataTransfer.files[0]); }}
                onClick={() => !file && fileInputRef.current?.click()}
              >
                <input type="file" ref={fileInputRef} className="hidden" accept=".csv,.xlsx,.xls" onChange={(e) => { if (e.target.files?.[0]) validateAndSetFile(e.target.files[0]); }} />

                {file ? (
                  <div className="flex flex-col items-center gap-3 w-full">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                      <FileCsv size={26} weight="duotone" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800 truncate px-4 max-w-xs">{file.name}</p>
                      <p className="text-xs font-semibold text-slate-400 mt-0.5">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="text-xs font-bold text-rose-500 hover:text-rose-600 transition-colors focus:outline-none"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                      <UploadSimple size={22} weight="duotone" />
                    </div>
                    <p className="text-sm font-bold text-slate-700">Drag & drop your file here</p>
                    <p className="text-xs font-medium text-slate-400 mt-1">or click to browse</p>
                    <p className="text-[10px] font-bold text-slate-300 mt-4 uppercase tracking-wider">
                      Supported: .csv · .xlsx · .xls
                    </p>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button onClick={fetchColumns} disabled={!file || isLoadingColumns} className="shadow-sm">
                  {isLoadingColumns ? 'Loading columns...' : 'Next: Map Fields →'}
                </Button>
              </div>
            </div>

          ) : step === 'mapping' ? (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-slate-500 font-medium">
                Map your CSV columns to the database fields. Required fields are marked with <span className="text-rose-500">*</span>.
                {importType === 'profile' && (
                  <span className="text-slate-400"> Auto-matched fields are shown first.</span>
                )}
              </p>

              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                {/* Always-visible: required + auto-matched */}
                {visibleFields.map((field) => (
                  <MappingSelect key={field.key} field={field} />
                ))}
              </div>

              {/* Show all toggle for profile (80+ fields) */}
              {importType === 'profile' && optionalUnmapped.length > 0 && (
                <button
                  onClick={() => setShowAllFields((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-accent transition-colors mx-auto"
                >
                  <CaretDown
                    size={13}
                    weight="bold"
                    className={`transition-transform duration-200 ${showAllFields ? 'rotate-180' : ''}`}
                  />
                  {showAllFields
                    ? 'Hide optional fields'
                    : `Show ${optionalUnmapped.length} more optional fields`}
                </button>
              )}

              <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
                <Button onClick={handleAnalyze} disabled={isAnalyzing} className="shadow-sm">
                  {isAnalyzing ? 'Analyzing...' : 'Next: Analyze Data →'}
                </Button>
              </div>
            </div>

          ) : step === 'analysis' ? (
            <div className="flex flex-col gap-5">
              <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 mx-auto mb-3">
                  <Check size={24} weight="bold" />
                </div>
                <h3 className="font-outfit font-bold text-slate-800 text-lg mb-1">Analysis Complete</h3>
                <p className="text-xs text-slate-500 font-medium mb-5">
                  Found <span className="font-bold text-slate-800">{analysisResult?.total || 0}</span> valid records in your file.
                </p>

                <div className="flex justify-center gap-6 border-t border-slate-200 pt-5 flex-wrap">
                  <div className="text-center">
                    <p className="text-3xl font-black text-emerald-600 mb-1">{analysisResult?.new || 0}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {importType === 'payments' ? 'Will Insert' : 'New Donors'}
                    </p>
                  </div>
                  <div className="w-px bg-slate-200" />
                  <div className="text-center">
                    <p className="text-3xl font-black text-slate-900 mb-1">{analysisResult?.existing || 0}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {importType === 'payments' ? 'Already in DB' : 'Existing Donors'}
                    </p>
                  </div>
                  {importType === 'payments' && !!(analysisResult?.missing_donor) && (
                    <>
                      <div className="w-px bg-slate-200" />
                      <div className="text-center">
                        <p className="text-3xl font-black text-amber-500 mb-1">{analysisResult.missing_donor}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Missing Donor</p>
                      </div>
                    </>
                  )}
                </div>
                {importType === 'payments' && !!(analysisResult?.missing_donor) && (
                  <p className="text-[11px] text-amber-600 font-medium bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3 text-center">
                    ⚠️ {analysisResult.missing_donor} payment{analysisResult.missing_donor !== 1 ? 's' : ''} will be skipped — their donor profiles haven't been imported yet. Import those donors first via <span className="font-bold">Profile Data</span> import.
                  </p>
                )}
              </div>

              {!!analysisResult?.existing && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-bold text-slate-800 mb-3">
                    How should we handle existing {importType === 'payments' ? 'payments' : 'donors'}?
                  </p>
                  {[
                    { value: true, title: 'Update with new data', desc: 'Overwrite existing records with data from this file' },
                    { value: false, title: 'Skip existing records', desc: 'Only import new records, leave existing ones untouched' },
                  ].map(({ value, title, desc }) => (
                    <label
                      key={String(value)}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-smooth ${
                        updateExisting === value
                          ? 'border-accent bg-accent/5'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="update_existing"
                        checked={updateExisting === value}
                        onChange={() => setUpdateExisting(value)}
                        className="mt-0.5 text-accent focus:ring-accent"
                      />
                      <div>
                        <p className="text-xs font-bold text-slate-700">{title}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                <Button variant="outline" onClick={() => setStep('mapping')}>Back to Mapping</Button>
                <Button onClick={handleUpload} variant="accent" className="shadow-sm shadow-accent/20">
                  Start Import
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
